import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { ConfirmDialogProvider } from "@/components/ConfirmDialogProvider";
import Layout from "@/components/Layout";
import { loadPage } from "@/lib/routePreload";
import { canAccessRoleGate } from "@/lib/roles";

const LoginPage = lazy(() => loadPage("/login"));
const ForgotPasswordPage = lazy(() => loadPage("/forgot-password"));
const ResetPasswordPage = lazy(() => loadPage("/reset-password"));
const SetupAccountPage = lazy(() => loadPage("/setup-account"));
const DashboardPage = lazy(() => loadPage("/"));
const ProjectsPage = lazy(() => loadPage("/projects"));
const ProjectDetailPage = lazy(() => loadPage("/projects/:id"));
const UsersPage = lazy(() => loadPage("/users"));
const ReportDashboardPage = lazy(() => loadPage("/reports"));
const ProjectStatusReportPage = lazy(() => loadPage("/project-status-report"));
const DeadlineDashboardPage = lazy(() => loadPage("/deadlines"));
const StaffCalendarPage = lazy(() => loadPage("/calendar"));
const SubContractorsPage = lazy(() => loadPage("/sub-contractors"));
const TasksPage = lazy(() => loadPage("/tasks"));
const AssignmentTrackerPage = lazy(() => loadPage("/tracker"));
const AccountingDashboardPage = lazy(() => loadPage("/accounting"));
const AccountingAlertsPage = lazy(() => loadPage("/accounting-alerts"));
const ClientsPage = lazy(() => loadPage("/clients"));

// Module-level role arrays — passing inline `[…]` to `<ProtectedRoute>` would
// create a new array on every render and break any future React.memo-ization
// of the route component.
const PROJECT_STATUS_REPORT_ROLES = ["project_manager", "accounting"];
const ADMIN_ACCOUNTING_ROLES = ["accounting"];
const PROJECT_MANAGER_ROLES = ["project_manager"];

function FullPageLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F7F8F6]">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 border-2 border-[#284A36] border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-[#647069]">Loading...</p>
      </div>
    </div>
  );
}

function PageLoading() {
  return (
    <div className="min-h-[55vh] flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="h-7 w-7 border-2 border-[#284A36] border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-[#647069]">Loading...</p>
      </div>
    </div>
  );
}

function ProtectedLayout() {
  const { user, loading } = useAuth();
  if (loading) {
    return <FullPageLoading />;
  }
  if (!user) return <Navigate to="/login" />;
  return (
    <Layout>
      <Suspense fallback={<PageLoading />}>
        <Outlet />
      </Suspense>
    </Layout>
  );
}

function RoleGate({ children, adminOnly = false, allowedRoles = null, allowReviewer = true }) {
  const { user } = useAuth();
  return canAccessRoleGate(user, { adminOnly, allowedRoles, allowReviewer })
    ? children
    : <Navigate to="/" />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" />;
  return <Suspense fallback={<FullPageLoading />}>{children}</Suspense>;
}

function App() {
  return (
    <AuthProvider>
      <ConfirmDialogProvider>
        <BrowserRouter>
        <Routes>
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
          <Route path="/setup-account" element={<PublicRoute><SetupAccountPage /></PublicRoute>} />
          <Route element={<ProtectedLayout />}>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route path="/reports" element={<RoleGate allowedRoles={PROJECT_MANAGER_ROLES}><ReportDashboardPage /></RoleGate>} />
            <Route path="/project-status-report" element={<RoleGate allowedRoles={PROJECT_STATUS_REPORT_ROLES}><ProjectStatusReportPage /></RoleGate>} />
            <Route path="/deadlines" element={<DeadlineDashboardPage />} />
            <Route path="/calendar" element={<RoleGate allowReviewer={false}><StaffCalendarPage /></RoleGate>} />
            <Route path="/sub-contractors" element={<RoleGate allowedRoles={PROJECT_MANAGER_ROLES}><SubContractorsPage /></RoleGate>} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/tracker" element={<RoleGate allowedRoles={PROJECT_MANAGER_ROLES}><AssignmentTrackerPage /></RoleGate>} />
            <Route path="/accounting" element={<RoleGate allowedRoles={ADMIN_ACCOUNTING_ROLES}><AccountingDashboardPage /></RoleGate>} />
            <Route path="/accounting-alerts" element={<RoleGate allowedRoles={ADMIN_ACCOUNTING_ROLES}><AccountingAlertsPage /></RoleGate>} />
            <Route path="/clients" element={<RoleGate allowedRoles={PROJECT_MANAGER_ROLES}><ClientsPage /></RoleGate>} />
            <Route path="/users" element={<RoleGate allowedRoles={PROJECT_MANAGER_ROLES} allowReviewer={false}><UsersPage /></RoleGate>} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      </ConfirmDialogProvider>
    </AuthProvider>
  );
}

export default App;
